import { useState } from "react";
import { SafePaddingContainer } from "@/components/SafePaddingContainer";
import { Button } from "@/components/ui/button";
import { VerifyPaymentMethod } from "@/components/VerifyPaymentMethod";
import { PersistentData } from "@/helpers/PersistentData";
import { verifyNumber } from "@/bridge/verifyNumber";
import { signIn } from "@/bridge/signIn";
import { getUserCustomToken } from "@/bridge/getUserCustomToken";
import { signInWithCustomToken } from "firebase/auth";
import { updateProfile } from "firebase/auth";
import { auth } from "@/helpers/firebase";
import { callFirebaseFunction } from "@/bridge/callFirebaseFunction";
import { useIsLocationAllowed } from "@/hooks/useIsLocationAllowed";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { useSwipeDetection } from "@/hooks/useSwipeDetection";
import { useViewTransition } from "@/hooks/useViewTransition";


export function VerifyAccountPage() {
  const [code, setCode] = useState("");
  const [showVerifyPhoneNumber, setShowVerifyPhoneNumber] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocationAllowed] = useIsLocationAllowed();
  const { navigateBack } = useViewTransition();

  const handleBack = () => {
    if (!showVerifyPhoneNumber) {
      return;
    }
    navigateBack();
  };

  useSwipeDetection({
    minSwipeDistance: 50,
    onSwipeRight: () => {
      handleBack();
    }
  });

  async function handlePhoneVerification(code: string, verificationId: string | null) {
    if (!verificationId) {
      throw new Error("Missing verification ID");
    }
    
    await signIn({
      verificationId,
      verificationCode: code,
    });
  }
  
  async function handleProfileSetup(name: string | null, isLocationAllowed?: { locationName: string }) {
    if (!name) {
      throw new Error("Missing user name");
    }
    
    await Promise.all([
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      updateProfile(auth.currentUser!, { displayName: name }),
      callFirebaseFunction<{ name: string }, { success: boolean }>("updateName", { name }),
      callFirebaseFunction<{ state: string }, { success: boolean }>("updateStateLocation", {
        state: isLocationAllowed?.locationName ?? "Unknown",
      })
    ]);
  }
  
  async function handleSignInFlow(code: string): Promise<boolean> {
    try {
      const verificationId = await PersistentData.get("verificationId");

      if (!verificationId) {
        console.error("Failed to get verification ID");
        setErrorMessage("Failed to get verification ID");
        return false;
      }

      await handlePhoneVerification(code, verificationId);
      const name = await PersistentData.get("firstName");

      // if (!name) {
      //   console.error("Failed to get name");
      //   setErrorMessage("Failed to get name");
      //   return false;
      // }
      const token = await getUserCustomToken();
      if (!token) {
        throw new Error("Failed to get custom token");
      }
    
      await signInWithCustomToken(auth, token);

      if (name) {
        await handleProfileSetup(name, isLocationAllowed);
      }
      setShowVerifyPhoneNumber(false);
      return true;
    } catch (error) {
      // should probably show an error/handle with sentry/post
      setErrorMessage(error instanceof Error ? error.message : "Sign in failed");
      return false;
    }
  }

  const resendCode = async () => {
    const phoneNumber = await PersistentData.get("phoneNumber");
    if (!phoneNumber) {
      setErrorMessage("Failed to get phone number");
      return;
    }

    try {
      await verifyNumber(phoneNumber);
    } catch (error) {
      // should probably show an error/handle with sentry/post
      setErrorMessage(
        (error as unknown as Error).message ?? "An error occurred."
      );
      return;
    }
  }

  const handleCodeSubmit = () => {
      if (code.length === 0) {
        setErrorMessage("Please enter the code.");
        return;
      }

      setIsLoading(true);
      (async () => {
        const success = await handleSignInFlow(code);
        if (!success) {
          return;
        }
      })().finally(() => {
        setIsLoading(false);
      });
    }
  
    return (
      <div className="flex flex-col h-screen overflow-y-scroll pb-40 bg-blue-dark">
        <SafePaddingContainer orientation="header" className="bg-blue-dark h-full">
          <h1 className="text-3xl font-bold text-white">
            Final step, verify
          </h1>
          <p className="text-lg text-white font-semibold mt-4">
            Because we're a regulated app with real cash prizes, we need to verify your phone number and that you're 18+
          </p>

          { showVerifyPhoneNumber ?
            <>
              <div className="flex gap-4 w-full mt-6">
                <input
                  type="number"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-2/3 h-12 border border-slate-300 rounded px-3 font-semibold py-2 placeholder:text-gray-400 placeholder:font-bold focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-transparent text-xl"
                  placeholder="6 digit code" />
                <Button className='bg-blue text-white font-semibold text-xl h-12 w-1/4' onClick={() => handleCodeSubmit()}>
                  Next
                </Button>
              </div>
              <Button variant='wallet-btn' className="bg-slate mt-3 text-sm font-bold" onClick={() => resendCode()}>
                Resend code
              </Button>
              {errorMessage && (
                <p className="text-sm text-red-500 font-medium mt-1">
                  {errorMessage}
                </p>
              )}
            </> : <VerifyPaymentMethod />
          }
          <SafePaddingContainer orientation="footer" className="h-full">
            <div className='flex justify-center items-center h-1/2 text-white opacity-70 text-2xl font-semibold w-full 
            whitespace-nowrap btn-bounce-subtle'>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
              {
                // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                showVerifyPhoneNumber ? <h1 onClick={() => handleBack()}>Change phone number</h1> : null
              }
            </div>
          </SafePaddingContainer>
        {isLoading && <LoadingOverlay />}
        </SafePaddingContainer>
      </div>
    );
  }

  